import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Form, Table, Spinner, Badge } from 'react-bootstrap';
import {
  fetchSequences,
  createSequence,
  updateSequence,
  deleteSequence,
  duplicateSequence,
  enrollOpportunities,
  fetchSequenceEnrollments,
} from '../../../redux/actions/sequenceAction/SequenceAction';
import { getFilteredCRMSAction } from '../../../redux/actions/crmActions/CRMAction';
import './SequenceBuilder.scss';
import editIcon from '../../../assets/images/pencil.png';
import copyIcon from '../../../assets/images/copy.svg';
import deleteIcon from '../../../assets/images/delete.png';

function SequenceBuilder() {
  const dispatch = useDispatch();
  const { sequences, isLoadingSequences, isCreatingSequence, isUpdatingSequence, isEnrolling, sequenceEnrollments, isLoadingEnrollments } = useSelector(
    (state) => state.sequenceReducer
  );
  const crmsData = useSelector((state) => state.crms);
  const CRMS = crmsData?.docs?.docs || [];

  const [showModal, setShowModal] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    steps: [{ stepNumber: 1, name: '', delayDays: 0, messageTemplate: '', useAIGeneration: false }],
  });

  // Enrollment modal state
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollingSequence, setEnrollingSequence] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);

  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSequence, setProgressSequence] = useState(null);

  useEffect(() => {
    dispatch(fetchSequences());
    dispatch(getFilteredCRMSAction(1, {})); // Fetch all opportunities
  }, [dispatch]);

  const handleOpenModal = (sequence = null) => {
    if (sequence) {
      setEditingSequence(sequence);
      setFormData({
        name: sequence.name,
        description: sequence.description || '',
        steps: sequence.steps.map(step => ({ ...step })),
      });
    } else {
      setEditingSequence(null);
      setFormData({
        name: '',
        description: '',
        steps: [{ stepNumber: 1, name: '', delayDays: 0, messageTemplate: '', useAIGeneration: false }],
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSequence(null);
  };

  const handleAddStep = () => {
    setFormData({
      ...formData,
      steps: [
        ...formData.steps,
        {
          stepNumber: formData.steps.length + 1,
          name: '',
          delayDays: 0,
          messageTemplate: '',
          useAIGeneration: false,
        },
      ],
    });
  };

  const handleRemoveStep = (index) => {
    if (formData.steps.length > 1) {
      const newSteps = formData.steps.filter((_, i) => i !== index);
      // Renumber steps
      newSteps.forEach((step, i) => {
        step.stepNumber = i + 1;
      });
      setFormData({ ...formData, steps: newSteps });
    }
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = value;
    setFormData({ ...formData, steps: newSteps });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      alert('Please enter a sequence name');
      return;
    }

    if (formData.steps.some(step => !step.name.trim() || !step.messageTemplate.trim())) {
      alert('All steps must have a name and message template');
      return;
    }

    const sequenceData = {
      name: formData.name,
      description: formData.description,
      steps: formData.steps,
    };

    if (editingSequence) {
      dispatch(updateSequence(editingSequence._id, sequenceData, () => {
        handleCloseModal();
      }));
    } else {
      dispatch(createSequence(sequenceData, () => {
        handleCloseModal();
      }));
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this sequence?')) {
      dispatch(deleteSequence(id));
    }
  };

  const handleDuplicate = (id) => {
    dispatch(duplicateSequence(id));
  };

  // Enrollment handlers
  const handleOpenEnrollModal = (sequence) => {
    setEnrollingSequence(sequence);
    setSelectedLeads([]);
    setShowEnrollModal(true);
  };

  const handleCloseEnrollModal = () => {
    setShowEnrollModal(false);
    setEnrollingSequence(null);
    setSelectedLeads([]);
  };

  const handleToggleLead = (leadId) => {
    setSelectedLeads((prev) => {
      if (prev.includes(leadId)) {
        return prev.filter((id) => id !== leadId);
      }
      return [...prev, leadId];
    });
  };

  const handleEnrollLeads = () => {
    if (selectedLeads.length === 0) {
      alert('Please select at least one lead');
      return;
    }

    const enrollmentData = {
      sequenceId: enrollingSequence._id,
      opportunityIds: selectedLeads,
    };

    dispatch(
      enrollOpportunities(enrollmentData, () => {
        handleCloseEnrollModal();
        dispatch(fetchSequences()); // Refresh sequences to update enrolled count
      })
    );
  };

  // Progress modal handlers
  const handleOpenProgressModal = (sequence) => {
    console.log('🔍 Opening progress modal for sequence:', sequence._id, sequence.name);
    setProgressSequence(sequence);
    setShowProgressModal(true);
    dispatch(fetchSequenceEnrollments(sequence._id));
  };

  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
    setProgressSequence(null);
  };

  return (
    <div className="sequence-builder-container">
      <div className="common-title">SEQUENCE BUILDER</div>
      <div className="sequence-builder-content">
        <div className="sequence-header">
          <p className="subtitle">
            Create automated LinkedIn outreach sequences with custom messaging and timing
          </p>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="fas fa-plus"></i> Create New Sequence
          </Button>
        </div>

        {isLoadingSequences ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : sequences?.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-project-diagram fa-3x mb-3"></i>
            <h4>No Sequences Yet</h4>
            <p>Create your first LinkedIn outreach sequence to get started</p>
            <Button variant="primary" onClick={() => handleOpenModal()}>
              Create Sequence
            </Button>
          </div>
        ) : (
          <Table responsive hover className="sequences-table">
            <thead>
              <tr>
                <th>Sequence Name</th>
                <th>Steps</th>
                <th>Enrolled</th>
                <th>Active</th>
                <th>Response Rate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sequences?.map((sequence) => (
                <tr key={sequence._id}>
                  <td>
                    <div className="sequence-name">
                      <strong>{sequence.name}</strong>
                      {sequence.description && (
                        <small className="text-muted d-block">{sequence.description}</small>
                      )}
                    </div>
                  </td>
                  <td>{sequence.steps?.length || 0} steps</td>
                  <td>{sequence.stats?.totalEnrolled || 0}</td>
                  <td>
                    <Badge bg={sequence.isActive ? 'success' : 'secondary'}>
                      {sequence.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>
                    {sequence.stats?.responseRate !== undefined
                      ? `${sequence.stats.responseRate.toFixed(1)}%`
                      : 'N/A'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="enroll-button"
                        title="Enroll Leads"
                        onClick={() => handleOpenEnrollModal(sequence)}
                      >
                        +
                      </button>
                      <button
                        className="progress-button"
                        title="View Progress"
                        onClick={() => handleOpenProgressModal(sequence)}
                      >
                        <i className="fas fa-chart-line"></i>
                      </button>
                      <img
                        src={editIcon}
                        className="action-icon"
                        title="Edit"
                        onClick={() => handleOpenModal(sequence)}
                        alt="Edit"
                      />
                      <img
                        src={copyIcon}
                        className="action-icon"
                        title="Duplicate"
                        onClick={() => handleDuplicate(sequence._id)}
                        alt="Duplicate"
                      />
                      <img
                        src={deleteIcon}
                        className="action-icon"
                        title="Delete"
                        onClick={() => handleDelete(sequence._id)}
                        alt="Delete"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" className="sequence-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingSequence ? 'Edit Sequence' : 'Create New Sequence'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Sequence Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., LinkedIn Connection Follow-up"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional description of this sequence"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            <div className="steps-section">
              <h5>Sequence Steps</h5>
              {formData.steps.map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-header">
                    <h6>Step {step.stepNumber}</h6>
                    {formData.steps.length > 1 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => handleRemoveStep(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    )}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Step Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Initial Connection Request"
                      value={step.name}
                      onChange={(e) => handleStepChange(index, 'name', e.target.value)}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Delay Days {index === 0 ? '(usually 0 for first step)' : '*'}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={step.delayDays}
                      onChange={(e) =>
                        handleStepChange(index, 'delayDays', parseInt(e.target.value) || 0)
                      }
                    />
                    <Form.Text className="text-muted">
                      Days to wait after previous step before executing this step
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Message Template *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Hi {{firstName}}, I noticed we're both in {{industry}}..."
                      value={step.messageTemplate}
                      onChange={(e) => handleStepChange(index, 'messageTemplate', e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted">
                      Use variables: {'{'}firstName{'}'}, {'{'}lastName{'}'}, {'{'}companyName{'}'}
                    </Form.Text>
                  </Form.Group>
                </div>
              ))}

              <Button variant="outline-primary" size="sm" onClick={handleAddStep} className="mt-2">
                <i className="fas fa-plus"></i> Add Step
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isCreatingSequence || isUpdatingSequence}
            >
              {isCreatingSequence || isUpdatingSequence ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingSequence ? 'Updating...' : 'Creating...'}
                </>
              ) : editingSequence ? (
                'Update Sequence'
              ) : (
                'Create Sequence'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Enrollment Modal */}
      {showEnrollModal && enrollingSequence && (
        <Modal show={showEnrollModal} onHide={handleCloseEnrollModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Enroll Leads in {enrollingSequence.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">
              Select leads to enroll in this sequence. They will receive the automated messages according to the sequence steps.
            </p>

            {CRMS && CRMS.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table hover>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CRMS.map((lead) => {
                      const isEnrolled = lead.activeSequenceId && lead.sequenceEnrollmentId;
                      const isDisabled = isEnrolled && lead.activeSequenceId !== enrollingSequence._id;

                      return (
                        <tr
                          key={lead._id}
                          onClick={() => !isDisabled && handleToggleLead(lead._id)}
                          style={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedLeads.includes(lead._id)}
                              onChange={() => !isDisabled && handleToggleLead(lead._id)}
                              disabled={isDisabled}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>
                            {lead.firstName} {lead.lastName}
                            {isEnrolled && (
                              <Badge bg="info" className="ms-2" style={{ fontSize: '10px' }}>
                                In Sequence
                              </Badge>
                            )}
                          </td>
                          <td>{lead.email}</td>
                          <td>{lead.companyName || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4">
                <p>No leads available. Please add leads in the CRM section first.</p>
              </div>
            )}

            <div className="mt-3">
              <strong>{selectedLeads.length}</strong> lead{selectedLeads.length !== 1 ? 's' : ''} selected
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEnrollModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEnrollLeads}
              disabled={isEnrolling || selectedLeads.length === 0}
            >
              {isEnrolling ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enrolling...
                </>
              ) : (
                `Enroll ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Progress Modal */}
      {showProgressModal && progressSequence && (
        <Modal show={showProgressModal} onHide={handleCloseProgressModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Sequence Progress: {progressSequence.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(() => {
              console.log('📊 Enrollment data in modal:', {
                isLoadingEnrollments,
                sequenceEnrollments,
                hasEnrollments: sequenceEnrollments?.enrollments,
                enrollmentsLength: sequenceEnrollments?.enrollments?.length,
              });
              return null;
            })()}
            {isLoadingEnrollments ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading enrollment data...</p>
              </div>
            ) : sequenceEnrollments && sequenceEnrollments.enrollments && sequenceEnrollments.enrollments.length > 0 ? (
              <>
                <div className="mb-3">
                  <strong>Total Enrolled:</strong> {sequenceEnrollments.totalEnrollments} lead{sequenceEnrollments.totalEnrollments !== 1 ? 's' : ''}
                </div>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Lead Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Current Step</th>
                      <th>Progress</th>
                      <th>Responded</th>
                      <th>Next Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sequenceEnrollments.enrollments.map((enrollment) => (
                      <tr key={enrollment._id}>
                        <td>
                          <strong>
                            {enrollment.lead?.firstName} {enrollment.lead?.lastName}
                          </strong>
                        </td>
                        <td>{enrollment.lead?.email || 'N/A'}</td>
                        <td>
                          <Badge
                            bg={
                              enrollment.status === 'active'
                                ? 'success'
                                : enrollment.status === 'completed'
                                ? 'info'
                                : enrollment.status === 'paused'
                                ? 'warning'
                                : 'secondary'
                            }
                          >
                            {enrollment.status}
                          </Badge>
                        </td>
                        <td>
                          Step {enrollment.currentStepNumber}: {enrollment.currentStepName}
                        </td>
                        <td>
                          {enrollment.completedSteps} / {enrollment.totalSteps} steps
                        </td>
                        <td>
                          {enrollment.hasResponded ? (
                            <Badge bg="success">
                              <i className="fas fa-check"></i> Responded
                            </Badge>
                          ) : (
                            <Badge bg="secondary">No Response</Badge>
                          )}
                        </td>
                        <td>
                          {enrollment.status === 'active' && enrollment.nextActionDate
                            ? new Date(enrollment.nextActionDate).toLocaleDateString()
                            : enrollment.status === 'completed'
                            ? 'Completed'
                            : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            ) : (
              <div className="text-center py-4">
                <i className="fas fa-inbox fa-3x mb-3 text-muted"></i>
                <h5>No Enrollments Yet</h5>
                <p>No leads have been enrolled in this sequence yet.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseProgressModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default SequenceBuilder;
